import queryString from 'query-string';
import * as R from 'ramda';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { MemoryRouter as Router } from 'react-router-dom';
import Log from '../common/logging';
import { Action, Provider, Resolver } from '../config';
import { Routes } from '../config/routes';
import ServiceErrorModal from '../screens/errors/ServiceErrorModal';
import setupNetworkRetries from '../services/common/networkRetries';
import { ServiceErrorType, UserErrorResponse, UserResponseOptions } from '../services/common/serviceError';
import ServiceErrorProvider from '../services/common/serviceErrorProvider';
import initialiseDevMode from './devMode';
import { ServiceErrorCallback, UserResponse } from './error/ServiceErrorCallback';
import UserResponseCallbackCache from './error/UserResponseCallbackCache';
import { GatekeeperNetworkServiceContext } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import Loading from '../common/components/Loading/Loading';

export interface MemoryRouterProps {
  queryParams: queryString.ParsedQuery<string>;
}

export const MemoryRouterContext = createContext<MemoryRouterProps>({} as MemoryRouterProps);

type MemoryRouterProviderProps = {
  children: JSX.Element;
};

// We need to wrap the MemoryRouter in order to handle query strings which the MemoryRouter does not support.
export function MemoryRouterProvider(props: MemoryRouterProviderProps): JSX.Element {
  const queryParams = queryString.parse(window.location.search);
  const [, setQueryParams] = useState<queryString.ParsedQuery<string>>();
  const [serviceError, setServiceError] = useState<ServiceErrorType | null>(null);
  const [errorResponseOptions, setErrorResponseOptions] = useState<UserResponseOptions>(UserResponseOptions.all);
  const [initialUrl, setInitialUrl] = useState<string | null>(null);
  const [errorResponseCallback] = useState<UserResponseCallbackCache>(new UserResponseCallbackCache());
  const { children } = props;
  // TODO handle unknown routes
  const {
    networkAddress: network,
    action,
    provider: providerParameter,
    gatekeeperSendsTransaction,
    ownerSigns: ownerSignsParameter,
    chain,
  } = queryParams || {};
  let provider: Provider | undefined;
  if (providerParameter && R.is(String, providerParameter)) {
    provider = Resolver.transformIncomingProvider(providerParameter as string);
  }

  const ACTIONS_WITHOUT_CHAIN = ['revoke']; // Special actions that don't require a chain parameter

  // validate the owner signs flag
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);
  const civicSendsEnabled = gknServiceData?.constraints?.public?.supportCivicSends;

  // If we don't have the required parameters, we can't do anything
  useEffect(() => {
    const allRequiredParamsEmpty = R.isNil(chain) && R.isNil(action) && R.isNil(network) && R.isNil(gknServiceData);
    if ((
        (!ACTIONS_WITHOUT_CHAIN.includes(action as string) || R.isNil(network)) && // First check if the action doesn't require a chain, and that we have a network address
        R.isNil(chain) && // If it requires a chain, also check that we have one
        !R.isNil(gknServiceData) // Finally, check that we have the GKN service data
      ) 
      || (
        allRequiredParamsEmpty
      )
  ) {
      setInitialUrl(Routes.INVALID_CONFIGURATION);
    }
  }, [gknServiceData]);

  useEffect(() => {
    // We can't do anything until we have the civicSendsDisabled flag and GKNS data
    if (
      !ACTIONS_WITHOUT_CHAIN.includes(action as string) && // First check if the action doesn't require a chain, and that we have a network address
      (R.isNil(civicSendsEnabled) || R.isNil(gknServiceData))
    ) {
      return;
    }
    const ownerSignsConfig = Resolver.validateOwnerSignsConfig(
      network as string,
      action as Action,
      ownerSignsParameter as string,
      gatekeeperSendsTransaction === 'true',
      chain as string,
      civicSendsEnabled!!,
    );
    // Get the initial route based on the network and action query parameters
    const url = ownerSignsConfig.isValid
      ? Resolver.getRoute(gknServiceData?.clientFlowType, action as Action, provider)
      : ownerSignsConfig.errorRoute || Routes.ERROR;
    setInitialUrl(url);
  }, [civicSendsEnabled]);

  const requestUserErrorFeedback = (
    errorType: ServiceErrorType,
    options: UserResponseOptions,
    resultCallback: UserResponse,
  ): void => {
    setServiceError(errorType);
    setErrorResponseOptions(options);
    errorResponseCallback.setCallback(resultCallback);
  };

  const onUserErrorResponse = (response: UserErrorResponse) => {
    setServiceError(null);
    errorResponseCallback.invoke(response);
  };

  useEffect(() => {
    setupNetworkRetries();
    initialiseDevMode(queryParams);
    setQueryParams(queryParams);
    ServiceErrorProvider.setServiceError(new ServiceErrorCallback(requestUserErrorFeedback));
    Log.debug(`Loading route for network:${network} action: ${action} provider: ${provider} => route: ${initialUrl}`);
  }, []);

  return (
    <MemoryRouterContext.Provider value={{ queryParams }}>
      {initialUrl ? (
        <Router initialEntries={[initialUrl]}>
          <ServiceErrorModal
            showModal={serviceError != null}
            errorType={serviceError}
            options={errorResponseOptions}
            onUserErrorResponse={onUserErrorResponse}
          />
          {children}
        </Router>
      ) : (
        <div className="document-fixed-height flex h-screen justify-center items-center w-full bg-background">
          <Loading dataTestId="IN_PROGRESS" />
        </div>
      )}
    </MemoryRouterContext.Provider>
  );
}
